import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
} from '@mui/material';
import { ContextMenuProps } from '../../types';

export function ContextMenu<T>({
  row,
  rowId,
  menuItems,
  anchorEl,
  anchorPosition,
  open,
  onClose,
}: ContextMenuProps<T>) {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const onCloseRef = React.useRef(onClose);

  // Keep onClose ref current
  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleMenuItemClick = async (
    action: (row: T, rowId: string | number) => void | Promise<void>,
  ) => {
    onClose();
    await action(row, rowId);
  };

  // Add global event listener for right-click outside
  React.useEffect(() => {
    if (!open) return;

    const handleGlobalContextMenu = (event: MouseEvent) => {
      // Check if the right-click is outside the menu
      if (menuRef.current) {
        const isInsideMenu = menuRef.current.contains(event.target as Node);

        if (!isInsideMenu) {
          event.preventDefault(); // Prevent default context menu
          onCloseRef.current();
        }
      }
    };

    const handleGlobalMouseDown = (event: MouseEvent) => {
      // Also handle mousedown with right button (button 2)
      if (event.button === 2 && menuRef.current) {
        const isInsideMenu = menuRef.current.contains(event.target as Node);

        if (!isInsideMenu) {
          onCloseRef.current();
        }
      }
    };

    // Add event listeners to document
    document.addEventListener('contextmenu', handleGlobalContextMenu);
    document.addEventListener('mousedown', handleGlobalMouseDown);

    return () => {
      document.removeEventListener('contextmenu', handleGlobalContextMenu);
      document.removeEventListener('mousedown', handleGlobalMouseDown);
    };
  }, [open]); // Removed onClose from dependencies to prevent infinite loop

  return (
    <Menu
      anchorEl={anchorEl}
      anchorReference={anchorPosition ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={anchorPosition}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          ref: menuRef,
        },
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      {menuItems.map((item, index) => {
        const isDisabled =
          item.disabled === true ||
          (typeof item.disabled === 'function' && item.disabled(row));

        return (
          <div key={item.id}>
            <MenuItem
              disabled={isDisabled}
              onClick={() => handleMenuItemClick(item.action)}
              sx={(theme) => ({
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2), // 20% opacity
                },
                transition: 'all 0.2s ease-in-out',
              })}
            >
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
            {item.divider && index < menuItems.length - 1 && <Divider />}
          </div>
        );
      })}
    </Menu>
  );
}
